"use client";

import Icon from "@/components/library/Icon";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import type {ComponentProps, HTMLAttributes, ReactNode} from "react";
import {
   createContext,
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import {useTranslation} from "react-i18next";
import {CodeBlockBody} from "./code-block-body";
import {
   createRawTokens,
   highlightCode,
   type HighlightLanguage,
   type TokenizedCode,
} from "./code-block-highlight";

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
   code: string;
   language: HighlightLanguage;
   showLineNumbers?: boolean;
   /** Rendered after the content, inside the container (e.g. expand toggles). */
   footer?: ReactNode;
   /** Overrides what the copy button puts on the clipboard (defaults to `code`). */
   copyText?: string;
};

interface CodeBlockContextType {
   code: string;
}

const CodeBlockContext = createContext<CodeBlockContextType>({
   code: "",
});

export const CodeBlockContainer = ({
   className,
   language,
   style,
   ...props
}: HTMLAttributes<HTMLDivElement> & {language: string}) => (
   <div
      className={cn(
         "group relative w-full overflow-hidden rounded-lg border border-neutral/70 bg-background text-900",
         className
      )}
      data-language={language}
      style={{
         containIntrinsicSize: "auto 200px",
         contentVisibility: "auto",
         ...style,
      }}
      {...props}
   />
);

export const CodeBlockHeader = ({
   children,
   className,
   ...props
}: HTMLAttributes<HTMLDivElement>) => (
   <div
      className={cn(
         "flex items-center justify-between border-b border-neutral/60 px-2.5 py-1.5 text-400 text-xs",
         className
      )}
      {...props}
   >
      {children}
   </div>
);

export const CodeBlockTitle = ({
   children,
   className,
   ...props
}: HTMLAttributes<HTMLDivElement>) => (
   <div className={cn("flex items-center gap-2", className)} {...props}>
      {children}
   </div>
);

export const CodeBlockActions = ({
   children,
   className,
   ...props
}: HTMLAttributes<HTMLDivElement>) => (
   <div className={cn("-my-1 -mr-1 flex items-center gap-2", className)} {...props}>
      {children}
   </div>
);

export const CodeBlockContent = ({
   code,
   language,
   showLineNumbers = false,
}: {
   code: string;
   language: HighlightLanguage;
   showLineNumbers?: boolean;
}) => {
   // Cold-path tokenization runs only in the effect below — never during
   // render — so StrictMode/concurrent re-renders can't double-tokenize.
   const rawTokens = useMemo(() => createRawTokens(code), [code]);
   const [highlighted, setHighlighted] = useState<{
      code: string;
      language: HighlightLanguage;
      tokens: TokenizedCode;
   } | null>(null);

   useEffect(() => {
      let cancelled = false;

      const cached = highlightCode(code, language, (result) => {
         if (!cancelled) {
            setHighlighted({code, language, tokens: result});
         }
      });
      if (cached) {
         setHighlighted({code, language, tokens: cached});
      }

      return () => {
         cancelled = true;
      };
   }, [code, language]);

   const tokenized =
      highlighted?.code === code && highlighted?.language === language
         ? highlighted.tokens
         : rawTokens;

   return (
      <div className="relative max-h-72 overflow-auto">
         <CodeBlockBody showLineNumbers={showLineNumbers} tokenized={tokenized} />
      </div>
   );
};

export const CodeBlock = ({
   code,
   language,
   showLineNumbers = false,
   footer,
   copyText,
   className,
   children,
   ...props
}: CodeBlockProps) => {
   const contextValue = useMemo(
      () => ({code: copyText ?? code}),
      [code, copyText]
   );

   return (
      <CodeBlockContext.Provider value={contextValue}>
         <CodeBlockContainer className={className} language={language} {...props}>
            {children}
            <CodeBlockContent
               code={code}
               language={language}
               showLineNumbers={showLineNumbers}
            />
            {footer}
         </CodeBlockContainer>
      </CodeBlockContext.Provider>
   );
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
   onCopy?: () => void;
   onError?: (error: Error) => void;
   timeout?: number;
};

export const CodeBlockCopyButton = ({
   onCopy,
   onError,
   timeout = 2000,
   children,
   className,
   ...props
}: CodeBlockCopyButtonProps) => {
   const [isCopied, setIsCopied] = useState(false);
   const timeoutRef = useRef<number>(0);
   const {code} = useContext(CodeBlockContext);
   const {t: ct} = useTranslation("common");

   const copyToClipboard = useCallback(async () => {
      if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
         onError?.(new Error("Clipboard API not available"));
         return;
      }

      try {
         if (!isCopied) {
            await navigator.clipboard.writeText(code);
            setIsCopied(true);
            onCopy?.();
            timeoutRef.current = window.setTimeout(() => setIsCopied(false), timeout);
         }
      } catch (error) {
         onError?.(error as Error);
      }
   }, [code, onCopy, onError, timeout, isCopied]);

   useEffect(
      () => () => {
         window.clearTimeout(timeoutRef.current);
      },
      []
   );

   const iconName = isCopied ? "check" : "copy";

   return (
      <Button
         aria-label={isCopied ? ct("copied") : ct("copy")}
         className={cn("shrink-0", className)}
         onClick={copyToClipboard}
         size="icon"
         variant="ghost"
         {...props}
      >
         {children ?? <Icon icon={iconName} className="h-3 w-3" />}
      </Button>
   );
};
